import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { DateTime } from "luxon";
import styled from "styled-components";
import { Heading, CardLine } from "../../pages/IndividualView";
import { CheckTable } from "../../pages/IndividualView/CheckStyledComponents";
import { formatDate } from "../../utils/formatters";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { H3 } from "../Heading";
import { Paragraph } from "../Paragraph";
import { Input } from "../Input";
import { Button } from "../Button";
import { Slider } from "../Slider";
import { Modal } from "../Modal";
import { DatePicker } from "../DatePicker";
import { useCreatePortalFormerName } from "../../data/useCreatePortalFormerName";
import { useEditPortalFormerName } from "../../data/useEditPortalFormerName";
import { useDeletePortalFormerName } from "../../data/useDeletePortalFormerName";
import { formerNameValidationSchema } from "../../utils/validationSchema";
import { showSuccess, showError } from "../../utils/error-handling";
import { FormerName, Profile } from "../../types/Profile";
import theme from "../../theme";

const linkStyle: React.CSSProperties = {
  color: theme.colors.link,
  cursor: "pointer",
  fontWeight: 600,
  fontFamily: '"Inter", sans-serif',
  fontSize: 14,
};

const HeaderCell = styled.th`
  ${({ theme: { colors } }: any) => `
    color: ${colors.gray[50]};
    background: ${colors.white};
    border-bottom: 1px solid ${colors.gray[20]};
  `}
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  padding: 12px 16px;
  white-space: nowrap;
`;

const BodyCell = styled.td`
  ${({ theme: { colors, space } }: any) => `
    padding: ${space[2]};
    border-bottom: 1px solid ${colors.gray[10]};
    color: ${colors.gray[60]};
    font-weight: 400;
    vertical-align: middle;
  `}
`;

const getDatePickerValue = (value?: string | null): Date | string | null => {
  if (!value) return null;

  const isoDate = DateTime.fromISO(value);
  return isoDate.isValid ? isoDate.toJSDate() : value;
};

const normalizeDateValue = (value: Date | string | null): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return DateTime.fromJSDate(value).toISODate() ?? "";
  }

  if (typeof value === "string") {
    const isoDate = DateTime.fromISO(value);
    if (isoDate.isValid) return isoDate.toISODate() ?? "";

    const formattedDate = DateTime.fromFormat(value, "dd/MM/yyyy");
    if (formattedDate.isValid) return formattedDate.toISODate() ?? "";

    return value;
  }

  return "";
};

export const PreviousNameSection = ({ profile }: { profile: Profile }) => {
  const formerNames = profile?.former_names ?? [];

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FormerName | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();

  const { mutateAsync: createFormerName } = useCreatePortalFormerName();
  const { mutateAsync: editFormerName } = useEditPortalFormerName();
  const { mutateAsync: deleteFormerName } = useDeletePortalFormerName();

  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: ["profile", profile?.id] });
  };

  const openAdd = () => {
    setEditingRecord(null);
    setIsEditOpen(true);
  };

  const openEdit = (record: FormerName) => {
    setEditingRecord(record);
    setIsEditOpen(true);
  };

  const closeSlider = () => {
    setIsEditOpen(false);
    setIsDeleteModalOpen(false);
  };

  const initialValues = {
    name: editingRecord?.name ?? "",
    changeDate: getDatePickerValue(editingRecord?.change_date),
    ceasedOn: getDatePickerValue(editingRecord?.ceased_on),
    reason: editingRecord?.reason ?? "",
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (editingRecord?.id) {
        await editFormerName({
          params: {
            profile: Number(profile?.id),
            name: values.name.trim(),
            ceased_on: normalizeDateValue(values.ceasedOn) || null,
            change_date: normalizeDateValue(values.changeDate) || null,
            reason: values.reason.trim(),
          } as any,
          id: editingRecord.id,
        });
      } else {
        await createFormerName({
          profile: Number(profile?.id),
          name: values.name.trim(),
          ceased_on: normalizeDateValue(values.ceasedOn) || null,
          reason: values.reason.trim(),
        } as any);
      }

      invalidateProfile();
      showSuccess(
        `Previous name ${editingRecord ? "updated" : "added"} successfully`,
      );
      closeSlider();
    } catch (e) {
      showError(
        e,
        `Failed to ${editingRecord ? "update" : "add"} previous name`,
      );
    }
  };

  const handleDelete = async () => {
    if (!editingRecord?.id || isDeleting) return;

    setIsDeleting(true);

    try {
      await deleteFormerName({ id: editingRecord.id });
      invalidateProfile();
      showSuccess("Previous name deleted successfully");
      closeSlider();
    } catch (e) {
      showError(e, "Failed to delete previous name");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      mt={3}
      background={theme.colors.white}
      borderRadius={12}
      p={16}
      border={`1px solid ${theme.colors.divider}`}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Heading fontSize={14}>Previous name</Heading>

        <span style={linkStyle} onClick={openAdd}>
          Add
        </span>
      </Flex>

      {formerNames.length === 0 ? (
        <CardLine>No previous names found.</CardLine>
      ) : (
        <CheckTable>
          <thead>
            <tr>
              <HeaderCell>Previous name</HeaderCell>
              <HeaderCell>Changed on</HeaderCell>
              <HeaderCell>Ceased on</HeaderCell>
              <HeaderCell>Reason</HeaderCell>
              <HeaderCell />
            </tr>
          </thead>

          <tbody>
            {formerNames.map((formerName, i) => (
              <tr key={formerName.id ?? i}>
                <BodyCell>{formerName.name || "-"}</BodyCell>
                <BodyCell>
                  {formerName.change_date
                    ? formatDate(formerName.change_date)
                    : "-"}
                </BodyCell>
                <BodyCell>
                  {formerName.ceased_on
                    ? formatDate(formerName.ceased_on)
                    : "-"}
                </BodyCell>
                <BodyCell>{formerName.reason || "-"}</BodyCell>
                <BodyCell>
                  <Paragraph
                    my={0}
                    style={{ ...linkStyle, textAlign: "right" }}
                    onClick={() => openEdit(formerName)}
                  >
                    Edit
                  </Paragraph>
                </BodyCell>
              </tr>
            ))}
          </tbody>
        </CheckTable>
      )}

      <Slider isOpen={isEditOpen} onRequestClose={closeSlider} width="600px">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={() =>
            formerNameValidationSchema(Boolean(editingRecord))
          }
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Box p={3}>
              <Flex justifyContent="space-between" alignItems="center" mb={3}>
                <H3 fontSize={16}>
                  {editingRecord ? "Edit Previous Name" : "Add Previous Name"}
                </H3>
              </Flex>

              <form onSubmit={formik.handleSubmit}>
                <Box mb={3}>
                  <Input
                    label="Previous name"
                    name="name"
                    isRequired
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    hasError={Boolean(formik.dirty && formik.errors.name)}
                    errorMessage={
                      (formik.dirty && (formik.errors.name as string)) || ""
                    }
                  />
                </Box>

                <Flex gap="16" mb={3}>
                  <Box flex={1}>
                    <DatePicker
                      label="Ceased on"
                      isRequired
                      value={formik.values.ceasedOn as any}
                      format="dd/MM/yyyy"
                      maxDate={DateTime.now().toJSDate()}
                      onChange={(date: any) =>
                        formik.setFieldValue("ceasedOn", date)
                      }
                      hasError={Boolean(
                        formik.dirty && formik.errors.ceasedOn,
                      )}
                      errorMessage={
                        (formik.dirty && (formik.errors.ceasedOn as string)) ||
                        ""
                      }
                    />
                  </Box>

                  {editingRecord && (
                    <Box flex={1}>
                      <DatePicker
                        label="Change date"
                        isRequired
                        value={formik.values.changeDate as any}
                        format="dd/MM/yyyy"
                        maxDate={DateTime.now().toJSDate()}
                        onChange={(date: any) =>
                          formik.setFieldValue("changeDate", date)
                        }
                        hasError={Boolean(
                          formik.dirty && formik.errors.changeDate,
                        )}
                        errorMessage={
                          (formik.dirty &&
                            (formik.errors.changeDate as string)) ||
                          ""
                        }
                      />
                    </Box>
                  )}
                </Flex>

                <Box mb={4}>
                  <Input
                    label="Reason"
                    name="reason"
                    isRequired
                    value={formik.values.reason}
                    onChange={formik.handleChange}
                    hasError={Boolean(formik.dirty && formik.errors.reason)}
                    errorMessage={
                      (formik.dirty && (formik.errors.reason as string)) || ""
                    }
                  />
                </Box>

                <Flex justifyContent="space-between" alignItems="center">
                  {editingRecord ? (
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => setIsDeleteModalOpen(true)}
                      isDisabled={formik.isSubmitting}
                    >
                      Delete
                    </Button>
                  ) : (
                    <span />
                  )}

                  <Flex gap="12">
                    <Button type="button" variant="text" onClick={closeSlider}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isDisabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                      }
                    >
                      {formik.isSubmitting ? "Saving..." : "Save"}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Box>
          )}
        </Formik>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete previous name?"
        content="This will permanently remove this previous name record."
        onModalConfirmClick={handleDelete}
        secondaryButtonText="Cancel"
        primaryButtonText={isDeleting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={isDeleting}
      />
    </Box>
  );
};
